import { useCallback } from 'react'

import { toast } from 'react-toastify'

import { BookingChargeDefaultsByType } from 'constants/EntityConstants/OtherChargeEntity'
import { BookingChargeType, CongestionZone } from 'generated/graphql'
import { TBookingAddressForm, TBookingChargeForm } from 'types/form-types'

import { isPostcodeInZone, isZoneApplicableToAddress } from './utils'

type IUseCongestionZonesChargeSyncProps = {
  collectionAddresses: TBookingAddressForm[]
  deliveryAddresses: TBookingAddressForm[]
  zones?: CongestionZone[]
  isUlezCharge?: boolean
}

export const useCongestionZonesChargeSync = ({
  collectionAddresses,
  deliveryAddresses,
  zones,
  isUlezCharge,
}: IUseCongestionZonesChargeSyncProps) => {
  const handleZonesChargesSync = useCallback(
    (charges: TBookingChargeForm[]) => {
      if (!isUlezCharge) {
        return
      }

      if (!zones?.length) return

      const allBookingAddresses = [...(collectionAddresses || []), ...(deliveryAddresses || [])]
      if (!allBookingAddresses.length) return

      const applicableZoneNames = new Set<string>()
      allBookingAddresses.forEach((address) => {
        zones?.forEach((zone) => {
          if (
            isPostcodeInZone(
              address?.postcode || null,
              zone.postcodes.map((p) => p.name),
            ) &&
            isZoneApplicableToAddress(
              zone as CongestionZone,
              address?.at as string,
              address?.by as string,
            )
          ) {
            applicableZoneNames.add(zone.name)
          }
        })
      })

      const indicesToRemove: number[] = []
      charges.forEach((charge, index) => {
        if (
          charge.type === BookingChargeType.SpecialZone &&
          !applicableZoneNames.has(charge.name)
        ) {
          indicesToRemove.push(index)
        }
      })

      indicesToRemove.reverse().forEach((index) => {
        const removedChargeName = charges[index].name
        charges.splice(index, 1)
        toast.info(`Automatic charge "${removedChargeName}" was removed.`)
      })

      applicableZoneNames.forEach((zoneName) => {
        if (
          !charges.some(
            (charge) => charge.type === BookingChargeType.SpecialZone && charge.name === zoneName,
          )
        ) {
          const zone = zones?.find((z) => z.name === zoneName)
          const specialZoneCharge = BookingChargeDefaultsByType[BookingChargeType.SpecialZone]
          if (zone && specialZoneCharge?.type) {
            charges.push({
              ...specialZoneCharge,
              name: zone.name,
              rate: zone.customerCharge,
            })
            toast.info(`Automatic charge "${zone.name}" was added.`)
          }
        }
      })
    },
    [collectionAddresses, deliveryAddresses, isUlezCharge, zones],
  )

  return { handleZonesChargesSync }
}
